import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { Btn } from "./Buttons";
import { NAV, PHONE, PHONE_HREF } from "../../data/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled || open ? "bg-white/90 py-3 shadow-[0_8px_30px_rgba(24,49,79,0.06)] backdrop-blur-xl" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Nexus Italia Group – Home">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-1 xl:flex" aria-label="Menu principale">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `nav-link relative px-3 py-2 text-[13px] font-semibold tracking-wide transition-colors ${isActive ? "text-navy is-active" : "text-slate2 hover:text-navy"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-5 xl:flex">
          <a href={PHONE_HREF} data-testid="header-phone-link" className="flex items-center gap-2 text-[13px] font-semibold text-navy hover:text-gold-ink">
            <Phone className="h-4 w-4 text-gold" strokeWidth={1.6} /> {PHONE}
          </a>
          <Btn to="/contatti#modulo" testId="header-request-btn" className="!px-5 !py-2.5">Richiedi un servizio</Btn>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 text-navy xl:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="h-[calc(100dvh-68px)] overflow-y-auto bg-white px-5 pb-10 pt-6 sm:px-8 xl:hidden"
          >
            <nav className="flex flex-col" aria-label="Menu mobile">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`}
                    className={({ isActive }) => `flex items-center justify-between border-b border-navy/10 py-4 text-2xl font-semibold ${isActive ? "text-navy" : "text-slate2"}`}
                  >
                    {n.label}
                    <span className="text-xs font-medium text-gold-ink">0{i + 1}</span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-4">
              <Btn to="/contatti#modulo" testId="mobile-request-btn">Richiedi un servizio</Btn>
              <Btn href={PHONE_HREF} variant="ghost" testId="mobile-phone-link">Chiama {PHONE}</Btn>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
